import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../config/supabase';
import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner from '../components/LoadingSpinner';
import { 
  HeartIcon, 
  BuildingIcon,
  CultureIcon, 
  ArtIcon, 
  MusicIcon, 
  TheaterIcon, 
  CinemaIcon, 
  FestivalIcon, 
  SportIcon, 
  WorkshopIcon, 
  MarketIcon, 
  ConferenceIcon, 
  NatureIcon, 
  FamilyIcon,
  CalendarIcon,
  LocationIcon,
  EuroIcon,
  WeatherIcon,
  AccessibleIcon,
  ShareIcon,
  LinkIcon,
  AlertIcon,
  ExternalLinkIcon
} from '../components/icons/NavIcons';

/**
 * Page de détail d'un événement
 * Affiche toutes les informations d'un événement spécifique
 */
const EventDetailPage = () => {
  const { eventId } = useParams();
  const { t, i18n } = useTranslation();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [isFavorite, setIsFavorite] = useState(false);
  const [favLoading, setFavLoading] = useState(false);
  const [shareStatus, setShareStatus] = useState('');
  const [copyUrlStatus, setCopyUrlStatus] = useState('');
  const [organizerProfile, setOrganizerProfile] = useState(null);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportMessage, setReportMessage] = useState('');
  const [reportLoading, setReportLoading] = useState(false);
  const [reportStatus, setReportStatus] = useState('');
  const [imageError, setImageError] = useState(false);
  const [loginWarning, setLoginWarning] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [hasSocialConfig, setHasSocialConfig] = useState(false);
  const [socialPublishLoading, setSocialPublishLoading] = useState(false);
  const [socialPublishStatus, setSocialPublishStatus] = useState('');
  const { user: authUser } = useAuth();

  // Récupère l'utilisateur connecté
  useEffect(() => {
    const fetchUser = async () => {
      const { data } = await supabase.auth.getUser();
      setUser(data?.user || null);
    };
    fetchUser();
  }, []);

  // Vérifie si l'utilisateur est admin (sans redirection)
  useEffect(() => {
    const checkAdmin = async () => {
      if (!authUser?.id) return;
      const { data } = await supabase
        .from('roles')
        .select('role')
        .eq('id', authUser.id)
        .single();
      setIsAdmin(data?.role === 'admin');
    };
    checkAdmin();
  }, [authUser]);

  // Vérifie si la commune de l'événement est incluse et a Facebook configuré
  useEffect(() => {
    const checkSocialConfig = async () => {
      if (!event?.code_commune_insee || !isAdmin) {
        setHasSocialConfig(false);
        return;
      }
      // Vérifier qu'au moins une config Facebook active existe pour ce code INSEE
      const { data: configs } = await supabase
        .from('commune_social_networks')
        .select('id')
        .eq('code_commune_insee', event.code_commune_insee)
        .eq('network', 'facebook')
        .eq('active', true)
        .not('page_id', 'is', null)
        .not('access_token', 'is', null)
        .limit(1);

      setHasSocialConfig(configs && configs.length > 0);
    };
    checkSocialConfig();
  }, [event, isAdmin]);

  useEffect(() => {
    const fetchEvent = async () => {
      setLoading(true);
      
      try {
        // Appel à la Edge Function
        const { data: functionData, error: functionError } = await supabase.functions.invoke('get-event-by-id', {
          body: { eventId }
        });

        if (functionError) {
          console.error('Erreur lors de l\'appel à la fonction:', functionError);
          setEvent(null);
          setLoading(false);
          return;
        }

        const eventData = functionData?.data;
        setEvent(eventData);
        setImageError(false); // Réinitialiser l'état d'erreur d'image
        
        // Si l'événement n'a pas d'organizer_name et a un organizer_id, récupérer le profil
        if (eventData && !eventData.organizer_name && eventData.organizer_id) {
          const { data: profileData } = await supabase
            .from('profiles')
            .select('organizer_name, organizer_company_name, organizer_url')
            .eq('id', eventData.organizer_id)
            .single();
          
          setOrganizerProfile(profileData);
        }
      } catch (err) {
        console.error('Exception lors du chargement de l\'événement:', err);
        setEvent(null);
      }
      
      setLoading(false);
    };
    fetchEvent();
  }, [eventId]);

  // Vérifie si l'événement est déjà en favori
  useEffect(() => {
    const checkFavorite = async () => {
      if (!user?.id || !event?.id) return;
      
      try {
        const { data, error } = await supabase
          .from('favorites')
          .select('id')
          .eq('user_id', user.id)
          .eq('event_id', event.id)
          .maybeSingle(); // Utilise maybeSingle() au lieu de single()
          
        if (error) {
          console.warn('Erreur lors de la vérification des favoris:', error);
          setIsFavorite(false);
          return;
        }
        
        setIsFavorite(!!data);
      } catch (err) {
        console.warn('Exception lors de la vérification des favoris:', err);
        setIsFavorite(false);
      }
    };
    
    if (event && user) checkFavorite();
  }, [event, user]);

  const getCategoryImage = (categoryName) => {
    if (!categoryName) return '';
    // Nettoie le nom pour le format de fichier (ex: "culture" => "culture.jpg")
    const fileName = categoryName.toLowerCase().replace(/[^a-z0-9]/g, '') + '.jpg';
    const imagePath = `/categories/${fileName}`;
    console.log('Chemin image catégorie recherché:', imagePath, 'pour catégorie:', categoryName);
    return imagePath;
  };

  // Fonction utilitaire pour formater la date complète traduite
  const formatFullDate = (dateStr, timeStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    // Utilise les codes JS pour le jour/mois, pour garantir la correspondance avec les clés de traduction
    const weekdayKey = [
      'sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'
    ][date.getDay()];
    const monthKey = [
      'january', 'february', 'march', 'april', 'may', 'june',
      'july', 'august', 'september', 'october', 'november', 'december'
    ][date.getMonth()];
    const weekday = t(`time.days.${weekdayKey}`);
    const day = date.getDate();
    const month = t(`time.months.${monthKey}`);
    const year = date.getFullYear();
    let hour = '';
    if (timeStr) {
      const [h, m] = timeStr.split(':');
      hour = `${h}h${m}`;
    }
    return `${weekday} ${day} ${month} ${year}${hour ? ' ' + hour : ''}`;
  };

  // Fonction pour ajouter à l'agenda compatible mobile (iOS/Android)
  const handleAddToCalendar = () => {
    if (!event) return;
    
    // Format ICS
    const pad = n => n.toString().padStart(2, '0');
    const formatDateTime = (date, time) => {
      if (!date) return '';
      const d = new Date(date + (time ? 'T' + time : ''));
      return (
        d.getUTCFullYear() +
        pad(d.getUTCMonth() + 1) +
        pad(d.getUTCDate()) +
        'T' +
        pad(d.getUTCHours()) +
        pad(d.getUTCMinutes()) +
        '00Z'
      );
    };
    
    const dtStart = formatDateTime(event.start_date, event.start_time);
    const dtEnd = formatDateTime(event.end_date || event.start_date, event.end_time);
    
    // Construction de l'adresse complète pour le champ LOCATION
    const locationParts = [
      event.formatted_address,
      event.city,
      event.postal_code
    ].filter(Boolean);
    const fullLocation = locationParts.join(', ');
    
    const ics = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//Maploop//EN',
      'BEGIN:VEVENT',
      `SUMMARY:${event.title}`,
      `DESCRIPTION:${event.description || ''}`,
      `LOCATION:${fullLocation}`,
      `DTSTART:${dtStart}`,
      `DTEND:${dtEnd}`,
      'END:VEVENT',
      'END:VCALENDAR'
    ].join('\r\n');

    // Téléchargement du fichier .ics
    const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${event.title || 'event'}.ics`;
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    
    setTimeout(() => {
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }, 100);
  };

  // Génère le lien Google Calendar
  const getGoogleCalendarUrl = () => {
    if (!event) return '#';
    const pad = n => n.toString().padStart(2, '0');
    const formatDateTime = (date, time) => {
      if (!date) return '';
      const d = new Date(date + (time ? 'T' + time : ''));
      return (
        d.getUTCFullYear() +
        pad(d.getUTCMonth() + 1) +
        pad(d.getUTCDate()) +
        'T' +
        pad(d.getUTCHours()) +
        pad(d.getUTCMinutes()) +
        '00Z'
      );
    };
    const dtStart = formatDateTime(event.start_date, event.start_time);
    const dtEnd = formatDateTime(event.end_date || event.start_date, event.end_time);
    
    // Construction de l'adresse complète pour le champ location
    const locationParts = [
      event.formatted_address,
      event.city,
      event.postal_code
    ].filter(Boolean);
    const fullLocation = locationParts.join(', ');
    
    const params = new URLSearchParams({
      action: 'TEMPLATE',
      text: event.title || '',
      dates: `${dtStart}/${dtEnd}`,
      details: event.description || '',
      location: fullLocation
    });
    return `https://calendar.google.com/calendar/render?${params.toString()}`;
  };

  // Ajoute ou retire des favoris
  const handleFavorite = async () => {
    if (!user?.id) {
      setLoginWarning(t('event.actions.loginRequiredFavorite'));
      setTimeout(() => setLoginWarning(''), 4000);
      return;
    }
    if (!event?.id) return;
    setFavLoading(true);
    if (isFavorite) {
      // Retirer des favoris
      await supabase
        .from('favorites')
        .delete()
        .eq('user_id', user.id)
        .eq('event_id', event.id);
      setIsFavorite(false);
    } else {
      // Ajouter aux favoris
      await supabase
        .from('favorites')
        .insert({
          user_id: user.id,
          event_id: event.id,
          created_at: new Date().toISOString()
        });
      setIsFavorite(true);
    }
    setFavLoading(false);
  };

  // Fonction de partage
  const handleShare = async () => {
    const shareUrl = window.location.href;
    const shareTitle = event?.title || '';
    const shareText = event?.description || '';
    if (navigator.share) {
      try {
        await navigator.share({
          title: shareTitle,
          text: shareText,
          url: shareUrl,
        });
        setShareStatus(t('event.actions.shareSuccess'));
      } catch (err) {
        setShareStatus(t('event.actions.shareError'));
      }
    } else if (navigator.clipboard) {
      try {
        await navigator.clipboard.writeText(shareUrl);
        setShareStatus(t('event.actions.copyUrlSuccess'));
      } catch (err) {
        setShareStatus(t('event.actions.copyUrlError'));
      }
    } else {
      setShareStatus(t('event.actions.shareNotSupported'));
    }
    setTimeout(() => setShareStatus(''), 3000);
  };

  // Fonction pour copier l'URL
  const handleCopyUrl = async () => {
    const currentUrl = window.location.href;
    try {
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(currentUrl);
        setCopyUrlStatus(t('event.actions.copyUrlSuccess'));
      } else {
        // Fallback pour les anciens navigateurs
        const textArea = document.createElement('textarea');
        textArea.value = currentUrl;
        textArea.style.position = 'fixed';
        textArea.style.left = '-9999px';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        setCopyUrlStatus(t('event.actions.copyUrlSuccess'));
      }
    } catch (err) {
      setCopyUrlStatus(t('event.actions.copyUrlError'));
    }
    setTimeout(() => setCopyUrlStatus(''), 3000);
  };

  // Fonction pour ouvrir le modal de signalement
  const handleReportClick = () => {
    if (!user?.id) {
      setLoginWarning(t('event.actions.loginRequiredReport'));
      setTimeout(() => setLoginWarning(''), 4000);
      return;
    }
    setShowReportModal(true);
  };

  // Fonction pour soumettre un signalement
  const handleSubmitReport = async () => {
    if (!user?.id || !event?.id || !reportMessage.trim()) {
      setReportStatus(t('event.actions.reportEmptyMessage'));
      setTimeout(() => setReportStatus(''), 3000);
      return;
    }
    
    setReportLoading(true);
    try {
      const { error } = await supabase
        .from('reports')
        .insert({
          event_id: event.id,
          user_id: user.id,
          message: reportMessage.trim(),
          created_at: new Date().toISOString()
        });
      
      if (error) throw error;
      
      setReportStatus(t('event.actions.reportSuccess'));
      setReportMessage('');
      setShowReportModal(false);
      setTimeout(() => setReportStatus(''), 3000);
    } catch (err) {
      console.error('Erreur lors du signalement:', err);
      setReportStatus(t('event.actions.reportError'));
      setTimeout(() => setReportStatus(''), 3000);
    } finally {
      setReportLoading(false);
    }
  };

  // Publier sur les réseaux sociaux (admin uniquement)
  const handleSocialPublish = async () => {
    if (!event?.id || !event?.code_commune_insee) return;
    setSocialPublishLoading(true);
    setSocialPublishStatus('');

    try {
      // Vérifier la config Facebook active pour le code INSEE de l'événement
      const { data: configs } = await supabase
        .from('commune_social_networks')
        .select('code_commune_insee, page_id, access_token')
        .eq('code_commune_insee', event.code_commune_insee)
        .eq('network', 'facebook')
        .eq('active', true);

      const validConfigs = configs?.filter(c => c.page_id && c.access_token) || [];
      if (validConfigs.length === 0) {
        setSocialPublishStatus('error:' + t('event.actions.socialPublishNoConfig'));
        setSocialPublishLoading(false);
        setTimeout(() => setSocialPublishStatus(''), 4000);
        return;
      }

      // Publier pour chaque commune configurée
      let successCount = 0;
      let lastError = '';

      for (const config of validConfigs) {
        // Créer le job
        const { data: job, error: jobError } = await supabase
          .from('social_publish_jobs')
          .insert({
            code_commune_insee: config.code_commune_insee,
            network: 'facebook',
            event_id: event.id,
            status: 'pending',
            scheduled_at: new Date().toISOString()
          })
          .select()
          .single();

        if (jobError) {
          lastError = jobError.message;
          continue;
        }

        // Appeler la Edge Function
        const { data: { session } } = await supabase.auth.getSession();
        const functionUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/publish-social`;

        const response = await fetch(functionUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session?.access_token}`,
            'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY
          },
          body: JSON.stringify({ job_id: job.id })
        });

        const result = await response.json();
        if (result.success) {
          successCount++;
        } else {
          lastError = result.error || 'Unknown error';
        }
      }

      if (successCount > 0) {
        setSocialPublishStatus('success:' + t('event.actions.socialPublishSuccess'));
      } else {
        setSocialPublishStatus('error:' + (lastError || t('event.actions.socialPublishError')));
      }
    } catch (err) {
      console.error('Social publish error:', err);
      setSocialPublishStatus('error:' + t('event.actions.socialPublishError'));
    } finally {
      setSocialPublishLoading(false);
      setTimeout(() => setSocialPublishStatus(''), 5000);
    }
  };

  // Mapping des catégories vers les icônes
  const categoryIcons = {
    'culture': CultureIcon,
    'art': ArtIcon,
    'music': MusicIcon,
    'theater': TheaterIcon,
    'cinema': CinemaIcon,
    'festival': FestivalIcon,
    'sport': SportIcon,
    'atelier': WorkshopIcon,
    'workshop': WorkshopIcon,
    'market': MarketIcon,
    'conference': ConferenceIcon,
    'nature': NatureIcon,
    'family': FamilyIcon
  };

  // Récupérer le composant d'icône pour la catégorie
  const CategoryIcon = event?.category?.name ? categoryIcons[event.category.name.toLowerCase()] : null;

  if (loading) {
    return <LoadingSpinner />;
  }
  if (!event) {
    return <div className="p-8 text-center text-red-600">{t('errors.eventNotFound')}</div>;
  }

  return (
    <div className="pb-20">
      {/* Image avec hauteur réduite */}
      <div className={`relative w-full ${event.image_url && !imageError ? 'h-[400px]' : 'h-[300px]'}`}>
        <img 
          src={(event.image_url && !imageError) ? event.image_url : (event.category?.name ? getCategoryImage(event.category.name) : '')}
          alt={event.title} 
          className="w-full h-full object-cover"
          onError={() => setImageError(true)}
        />
        {event.category && (
          <div className="absolute top-4 left-4 bg-white bg-opacity-80 px-4 py-2 rounded-full flex items-center">
            {CategoryIcon ? (
              <CategoryIcon 
                className="w-6 h-6 mr-2" 
                style={{ color: event.category?.color_code || '#2894D8' }}
              />
            ) : (
              <span className="mr-2">{event.category.icon || ''}</span>
            )}
            <span 
              className="font-medium"
              style={{ color: event.category?.color_code || '#2894D8' }}
            >
              {t(`categories.${event.category.name}`)}
            </span>
          </div>
        )}
        {(!event.image_url || imageError) && (
          <div className="absolute bottom-4 right-4 bg-white bg-opacity-70 px-2 py-0 rounded-full">
            <span className="text-black opacity-60 text-sm">
              {t('event.illustrationImage')}
            </span>
          </div>
        )}
      </div>
      
      {/* Contenu principal */}
      <div className="p-4">
        {/* Titre et description courte */}
        <h1 className="text-h1 mb-3">{event.title}</h1>
        
        {event.description && (
          <p className="text-body text-gray-700 mb-4 leading-relaxed">
            {event.description}
          </p>
        )}

        {/* Horaire avec icône et fond gris */}
        <div className="flex items-start gap-3 mb-3 p-3 bg-white rounded-xl">
          <CalendarIcon className="w-5 h-5 mt-0.5 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 mb-1">
              {event.start_date && event.end_date && event.start_date !== event.end_date
                ? t('event.period', {
                    start: formatFullDate(event.start_date, null),
                    end: formatFullDate(event.end_date, null)
                  })
                : event.start_date
                  ? formatFullDate(event.start_date, null)
                  : ''}
            </p>
            {event.start_time && (
              <p className="text-sm text-gray-600">
                {event.end_time 
                  ? `${t('common.from', { defaultValue: 'De' })} ${event.start_time.slice(0, 5)} ${t('common.to', { defaultValue: 'à' })} ${event.end_time.slice(0, 5)}`
                  : event.start_time.slice(0, 5)
                }
              </p>
            )}
          </div>
        </div>

        {/* Lieu avec icône et fond gris */}
        <div className="flex items-start gap-3 mb-4 p-3 bg-white rounded-xl">
          <LocationIcon className="w-5 h-5 mt-0.5 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            {event.location_name && (
              <p className="text-sm font-medium text-gray-900 mb-0">{event.location_name}</p>
            )}
            {event.formatted_address ? (
              <a
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                  `${event.formatted_address}${event.city ? `, ${event.city}` : ''}${event.postal_code ? ` ${event.postal_code}` : ''}`
                )}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-bleu1 underline hover:text-bleu3"
              >
                {event.formatted_address}
                {event.city && `, ${event.city}`}
                {event.postal_code && ` ${event.postal_code}`}
              </a>
            ) : (
              <p className="text-sm text-gray-600">
                {event.city && event.city}
                {event.postal_code && ` ${event.postal_code}`}
              </p>
            )}
          </div>
        </div>

        {/* Organisateur avec icône et fond gris */}
        <div className="flex items-start gap-3 mb-4 p-3 bg-white rounded-xl">
          <BuildingIcon className="w-5 h-5 mt-0.5 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 mb-1">
              {event.organizer_name ? (
                event.organizer_url ? (
                  <a href={event.organizer_url} target="_blank" rel="noopener noreferrer" className="text-bleu1 underline hover:text-bleu3">
                    {event.organizer_name}
                  </a>
                ) : (
                  event.organizer_name
                )
              ) : organizerProfile ? (
                <>
                  {organizerProfile.organizer_url ? (
                    <a href={organizerProfile.organizer_url} target="_blank" rel="noopener noreferrer" className="text-bleu1 underline hover:text-bleu3">
                      {organizerProfile.organizer_name || organizerProfile.organizer_company_name || '-'}
                    </a>
                  ) : (
                    organizerProfile.organizer_name || organizerProfile.organizer_company_name || '-'
                  )}
                </>
              ) : (
                '-'
              )}
            </p>
          </div>
        </div>

        {/* Badges caractéristiques */}
        <div className="flex flex-wrap gap-2 mb-6">
          {event.is_paid && (
            <span className="bg-gray-100 px-2 py-0.5 rounded-full text-xs border border-gray-200 text-gray-700 flex items-center gap-1">
              <EuroIcon className="w-4 h-4" />
              <span>{t('event.paid')}</span>
            </span>
          )}
          {event.weather_dependent && (
            <span className="bg-gray-100 px-2 py-0.5 rounded-full text-xs border border-gray-200 text-gray-700 flex items-center gap-1">
              <WeatherIcon className="w-4 h-4" />
              <span>{t('event.weatherDependent')}</span>
            </span>
          )}
          {event.is_recurring && (
            <span className="bg-gray-100 px-2 py-0.5 rounded-full text-xs border border-gray-200 text-gray-700 flex items-center gap-1">
              <span>🔄</span>
              <span>{t('event.recurring')}</span>
            </span>
          )}
          {event.is_accessible && (
            <span className="bg-gray-100 px-2 py-0.5 rounded-full text-xs border border-gray-200 text-gray-700 flex items-center gap-1">
              <AccessibleIcon className="w-4 h-4" />
              <span>{t('event.accessible')}</span>
            </span>
          )}
          {event.age_group?.name && (
            <span className="bg-gray-100 px-2 py-0.5 rounded-full text-xs border border-gray-200 text-gray-700">
              {t(`ageGroups.${event.age_group.name}`)}
            </span>
          )}
        </div>

        {/* URL externe si présente */}
        {event.external_url && (
          <a
            href={event.external_url}
            target="_blank"
            rel="noopener noreferrer"
            className="w-full mb-6 flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-bleu1 text-white text-base font-medium hover:bg-bleu3 hover:shadow-md transition-all duration-200"           
          >
            <ExternalLinkIcon className="w-5 h-5" />
            <span>{t('event.actions.externalURL')}</span>
          </a>
        )}

        {/* Grille d'actions 3x2 */}
        <div className="grid grid-cols-3 gap-3 mb-6">
          {/* Partager */}
          <button
            className="flex flex-col items-center justify-center gap-2 p-4 bg-white border border-bleu1 rounded-xl hover:bg-blue-50 hover:shadow-md transition-all duration-200"
            onClick={handleShare}
            type="button"
          >
            <ShareIcon className="w-6 h-6 text-bleu1" />
            <span className="text-xs text-bleu1 font-medium text-center">{t('event.actions.share')}</span>
          </button>

          {/* Copier URL */}
          <button 
            className="flex flex-col items-center justify-center gap-2 p-4 bg-white border border-bleu1 rounded-xl hover:bg-blue-50 hover:shadow-md transition-all duration-200"
            onClick={handleCopyUrl}
            type="button"
          >
            <LinkIcon className="w-6 h-6 text-bleu1" />
            <span className="text-xs text-bleu1 font-medium text-center">{t('event.actions.copyUrl')}</span>
          </button>

          {/* Favoris */}
          <button
            className="flex flex-col items-center justify-center gap-2 p-4 bg-white border border-bleu1 rounded-xl hover:bg-blue-50 hover:shadow-md transition-all duration-200 disabled:opacity-60 disabled:hover:bg-white"
            onClick={handleFavorite}
            disabled={favLoading}
            type="button"
          >
            <HeartIcon className={`w-6 h-6 ${isFavorite ? 'fill-red-500 text-red-500' : 'text-bleu1'}`} />
            <span className="text-xs text-bleu1 font-medium text-center">
              {isFavorite ? t('event.actions.removeFromFavorites') : t('event.actions.addToFavorites')}
            </span>
          </button>

          {/* Google Calendar */}
          <button
            className="flex flex-col items-center justify-center gap-2 p-4 bg-white border border-gray-300 rounded-xl hover:bg-gray-100 hover:shadow-sm transition-all duration-200"
            onClick={() => window.open(getGoogleCalendarUrl(), '_blank')}
            type="button"
          >
            <CalendarIcon className="w-6 h-6 text-gray-700" />
            <span className="text-xs text-gray-700 font-medium text-center">Google</span>
          </button>

          {/* Apple/Outlook Calendar */}
          <button
            className="flex flex-col items-center justify-center gap-2 p-4 bg-white border border-gray-300 rounded-xl hover:bg-gray-100 hover:shadow-sm transition-all duration-200"
            onClick={handleAddToCalendar}
            type="button"
          >
            <CalendarIcon className="w-6 h-6 text-gray-700" />
            <span className="text-xs text-gray-700 font-medium text-center">Apple</span>
          </button>

          {/* Signaler */}
          <button 
            className="flex flex-col items-center justify-center gap-2 p-4 bg-white border border-red-200 rounded-xl hover:bg-red-50 transition-all duration-200"
            onClick={handleReportClick}
            type="button"
          >
            <AlertIcon className="w-6 h-6 text-red-500" />
            <span className="text-xs text-red-500 font-medium text-center">{t('event.actions.report')}</span>
          </button>
        </div>

        {/* Bouton publication réseaux sociaux (admin + commune incluse & configurée) */}
        {isAdmin && hasSocialConfig && (
          <div className="mb-6">
            <button
              className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-bleu2 text-white text-base font-medium hover:bg-bleu3 hover:shadow-md transition-all duration-200 disabled:opacity-60"
              onClick={handleSocialPublish}
              disabled={socialPublishLoading}
              type="button"
            >
              <span>{socialPublishLoading ? '⏳' : '📱'}</span>
              <span>
                {socialPublishLoading
                  ? t('event.actions.socialPublishLoading')
                  : t('event.actions.socialPublish')}
              </span>
            </button>
            {socialPublishStatus && (
              <div className={`mt-2 text-sm text-center ${
                socialPublishStatus.startsWith('success:') ? 'text-green-600' : 'text-red-600'
              }`}>
                {socialPublishStatus.replace(/^(success|error):/, '')}
              </div>
            )}
          </div>
        )}

        {/* Messages de statut */}
        {loginWarning && (
          <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm text-center">
            {loginWarning}
          </div>
        )}
        {shareStatus && (
          <div className="text-green-600 text-sm text-center mb-3">{shareStatus}</div>
        )}
        {copyUrlStatus && (
          <div className="text-green-600 text-sm text-center mb-3">{copyUrlStatus}</div>
        )}
        {reportStatus && (
          <div className={`text-sm text-center mb-3 ${reportStatus.includes('succès') || reportStatus.includes('success') ? 'text-green-600' : 'text-red-600'}`}>
            {reportStatus}
          </div>
        )}
      </div>

      {/* Modal de signalement */}
      {showReportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h2 className="text-h2 mb-4">{t('event.actions.reportTitle')}</h2>
            <p className="text-body text-gray-600 mb-4">
              {t('event.actions.reportDescription')}
            </p>
            <textarea
              className="w-full border border-gray-300 rounded-lg p-3 mb-4 min-h-[120px] focus:outline-none focus:ring-2 focus:ring-bleu1"
              placeholder={t('event.actions.reportPlaceholder')}
              value={reportMessage}
              onChange={(e) => setReportMessage(e.target.value)}
              disabled={reportLoading}
            />
            <div className="flex gap-3 justify-end">
              <button
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
                onClick={() => {
                  setShowReportModal(false);
                  setReportMessage('');
                  setReportStatus('');
                }}
                disabled={reportLoading}
                type="button"
              >
                {t('common.cancel')}
              </button>
              <button
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors disabled:opacity-60"
                onClick={handleSubmitReport}
                disabled={reportLoading || !reportMessage.trim()}
                type="button"
              >
                {reportLoading ? t('common.sending') : t('event.actions.reportSubmit')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default EventDetailPage;


